import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from "react";
import {
  getConfig,
  ApiError,
  type ConfigBusinessProfile,
  type SecurityDepositMode,
} from "@fm/api";
import { getApiBaseUrl } from "../lib/config";

interface EmbedContextValue {
  apiKey: string;
  fleetId: string;
  stripePublishableKey: string;
  theme?: "light" | "dark";
  externalAccountId?: string;
  businessProfile?: ConfigBusinessProfile | null;
  securityDepositMode?: SecurityDepositMode;
  acceptCashDeposit?: boolean;
  configLoading: boolean;
  configError?: {
    message: string;
    code: string;
  };
}

const EmbedContext = createContext<EmbedContextValue | undefined>(undefined);

interface EmbedProviderProps {
  children: ReactNode;
  apiKey: string;
  fleetId: string;
  stripePublishableKey: string;
  theme?: "light" | "dark";
  apiBaseUrl?: string;
}

export function EmbedProvider({
  children,
  apiKey,
  fleetId,
  stripePublishableKey,
  theme = "light",
  apiBaseUrl,
}: EmbedProviderProps) {
  const [externalAccountId, setExternalAccountId] = useState<
    string | undefined
  >(undefined);
  const [businessProfile, setBusinessProfile] = useState<
    ConfigBusinessProfile | null | undefined
  >(undefined);
  const [securityDepositMode, setSecurityDepositMode] = useState<
    SecurityDepositMode | undefined
  >(undefined);
  const [acceptCashDeposit, setAcceptCashDeposit] = useState<
    boolean | undefined
  >(undefined);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<
    { message: string; code: string } | undefined
  >(undefined);

  useEffect(() => {
    const loadConfig = async () => {
      if (!apiKey) {
        setConfigLoading(false);
        return;
      }

      try {
        setConfigLoading(true);
        setConfigError(undefined);

        const apiClient = {
          baseUrl: getApiBaseUrl(apiBaseUrl),
          apiKey,
        };

        const response = await getConfig(apiClient);

        // The response IS the config data directly, not wrapped in { data: ... }
        if (response?.external_account_id) {
          setExternalAccountId(response.external_account_id);
        }
        if (response?.business_profile) {
          setBusinessProfile(response.business_profile);
        }
        if (response?.security_deposit_mode) {
          setSecurityDepositMode(response.security_deposit_mode);
        }
        if (response?.accept_cash_deposit !== undefined) {
          setAcceptCashDeposit(response.accept_cash_deposit);
        }
      } catch (error: unknown) {
        console.error("Failed to load config:", error);

        // `clientWithApiClient` throws an `ApiError` whose `.data` holds the
        // parsed response body. The backend FallbackController returns the
        // error envelope at the top level (`{ code, message, payload }`), so
        // read it from there. Some endpoints nest it under `error`, so we
        // check both shapes before falling back to a generic message.
        let errorMessage = "Failed to load configuration";
        let errorCode = "unknown_error";

        if (error instanceof ApiError) {
          const body = (error.data ?? {}) as {
            code?: string;
            message?: string;
            error?: { code?: string; message?: string };
          };
          errorCode = body.code ?? body.error?.code ?? errorCode;
          errorMessage =
            body.message ??
            body.error?.message ??
            error.message ??
            errorMessage;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        setConfigError({
          message: errorMessage,
          code: errorCode,
        });
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, [apiKey, apiBaseUrl]);

  const value = {
    apiKey,
    fleetId,
    stripePublishableKey,
    theme,
    externalAccountId,
    businessProfile,
    securityDepositMode,
    acceptCashDeposit,
    configLoading,
    configError,
  };

  return (
    <EmbedContext.Provider value={value}>{children}</EmbedContext.Provider>
  );
}

export function useEmbedContext() {
  const context = useContext(EmbedContext);
  if (context === undefined) {
    throw new Error("useEmbedContext must be used within an EmbedProvider");
  }
  return context;
}
